import { Suspense, lazy, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';

import { Button, Flex } from 'antd';

import dayjs from 'dayjs';
import isEmpty from 'lodash.isempty';

import axios from '../../axios';

import { capitalize, downloadCSV } from '../../helpers/common';
import { sortDates, sortNumbers, sortStrings } from '../../helpers/filter';
import { hasPermission } from '../../helpers/user';

import { BREEDING_EVENT_TYPE } from '../../constants/breeding';
import { DATE_FORMAT, EDIT_COMPONENT_TYPE } from '../../constants/common';
import { BLANK_FILTER_TEXT } from '../../constants/livestock';
import messages from '../../constants/messages';
import { PERMISSIONS } from '../../constants/permissions';
import { TABLE_CONFIG_COLUMNS, TABLE_CONFIG_KEYS } from '../../constants/table-configurations';
import { BREEDING_CSV_TEMPLATE } from '../../constants/templates';
import { BREEDING_CSV_VALIDATION_SCHEMA } from '../../constants/validation-schemas';

import { useModal } from '../../hooks/useModal';
import { useUnsavedChangesPrompt } from '../../hooks/useUnsavedChangesPrompt';

import { fetchAnimals } from '../../redux/actions/animal';
import { fetchBreeds } from '../../redux/actions/breed';
import {
  deleteBreedings,
  fetchBreeding,
  fetchBreedingEventStatusTypes,
  fetchBreedingEventTypes,
  setSortedData
} from '../../redux/actions/breeding';
import { createManyBreeding, updateBreeding } from '../../redux/actions/breedingSlice';
import { fetchMatingEventTypes } from '../../redux/actions/mating-event';
import { fetchMeasurementTypes } from '../../redux/actions/measurement';
import { fetchTreatmentTypes, fetchTreatmentUOM } from '../../redux/actions/treatment';
import { setSelectedFilters } from '../../redux/reducers/filters';

import AppTable from '../../components/AppTable';
import { COLUMN_SIZE } from '../../components/AppTable/constants';

import { errorToastHandler, infoToastHandler, successToastHandler, warnToastHandler } from '../action_notifier';
import { useFilterOptions } from './useFilterOptions';

const AppModal = lazy(() => import('../../components/AppModal'));
const CalvRegistrationModal = lazy(() => import('../../components/Modals/CalvRegistrationModal'));
const CreateBreedingModal = lazy(() => import('../../components/Breeding/CreateBreedingModal'));
const UnsavedChangesModal = lazy(() => import('../../components/ui/UnsavedChangesModal'));
const UploadModal = lazy(() => import('../../components/Modals/UploadModal'));

const commonColumnProperties = {
  ellipsis: true,
  sortDirections: ['ascend', 'descend']
};

function BreedingTable(props) {
  const dispatch = useDispatch();
  const history = useHistory();

  const formRef = useRef(null);
  const formCreateRef = useRef(null);

  const params = useParams();

  const { types: measurementTypes, loading: measurementTypesLoading } = useSelector(state => state.measurement);
  const { data: animals, calfAry, sireAry } = useSelector(state => state.animal);
  const user = useSelector(state => state.user);
  const { breedsSelectOptions } = useSelector(state => state.breed);
  const {
    breedingEventTypesSelectOptions,
    breedingEventStatusTypesSelectOptions,
    data: breeding,
    loading: breedingLoading,
    sortedData
  } = useSelector(state => state.breeding);
  const { selectedFilters } = useSelector(state => state.filters);
  const { treatmentTypesSelectOptions, treatmentUOMSelectOptions } = useSelector(state => state.treatment);
  const { selectOptions: matingTypes, loading: matingEventLoading } = useSelector(state => state.matingEvent);

  const [createFormSubmittable, setCreateFormSubmittable] = useState(false);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [initTableData, setInitTableData] = useState([]);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submittable, setSubmittable] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [state, setState] = useState({
    bulk_modal: false,
    calv_registration_animal: null,
    calv_registration_modal: false,
    common_data: {
      breeding_plan: '',
      breeding_status_id: [],
      calf_id: [],
      calving_performance: '',
      comments: '',
      cost: '0',
      event_description: '',
      event_end_date: '',
      event_start_date: '',
      event_type_id: [],
      expected_calving_date: '',
      is_pregnant: 0,
      maternity_performance: '',
      mating_type_id: [],
      sire_id: [],
      straw_ref: '',
      straws: ''
    },
    csv_modal: false,
    filters: {
      farms: [],
      geofences: [],
      labels: []
    },
    selectedIds: [],
    uploadSuccessMessage: '',
    mgmgList: []
  });

  const [uploadSuccessModalVisible, openUploadSuccessModal, closeUploadSuccessModal] = useModal();

  const [updatedDataSource, setUpdatedDataSource] = useState(undefined);

  useEffect(() => {
    fetchData();

    return () => {
      dispatch(setSelectedFilters({}));
    };
  }, []);

  useEffect(() => {
    if (breeding?.length) {
      setInitTableData([...breeding]);
      dispatch(setSortedData(breeding));

      prepareTableData();
    }
  }, [breeding?.length, matingTypes, sireAry, calfAry]);

  useEffect(() => {
    // on "Clear all filters" set to sortedData that used for calc filter options to original set of data
    if (isEmpty(filteredInfo) && breeding?.length) {
      dispatch(setSortedData(breeding));
    }
  }, [filteredInfo]);

  const toggleUploadModal = useCallback(() => {
    setState(prevState => ({ ...prevState, csv_modal: !prevState.csv_modal }));
    formRef.current?.resetFields();
  }, []);

  async function fetchData() {
    try {
      const promises = [
        getTreatmentTypes(),
        getTreatmentUOM(),
        getBreeding(),
        getMatingEventTypes(),
        dispatch(fetchBreedingEventTypes()),
        dispatch(fetchMeasurementTypes()).unwrap(),
        dispatch(fetchBreeds()),
        dispatch(fetchBreedingEventStatusTypes())
      ];

      if (!params?.id) {
        promises.push(getAnimals());
      }

      await Promise.all(promises);
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    } finally {
      setLoading(false);
    }
  }

  async function getAnimals() {
    try {
      await dispatch(fetchAnimals()).unwrap();
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  async function getMatingEventTypes() {
    try {
      await dispatch(fetchMatingEventTypes()).unwrap();
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  function getBreeding(query) {
    const animalId = params.id ? +params.id : null;
    const reqParams = animalId
      ? {
          livestock_ids: animalId,
          breeding_ids: animalId
        }
      : {
          query,
          farm_ids: state.filters?.farms?.map(x => x.value),
          label_ids: state.filters?.labels?.map(x => x.value),
          geofence_ids: state.filters?.geofences?.map(x => x.value)
        };

    dispatch(fetchBreeding(reqParams));
  }

  async function downloadCSVFile() {
    const ids = state?.selectedIds;
    const url = ids?.length ? `/breeding/export?breeding_ids=${ids}` : '/breeding/template_export';

    try {
      const response = await axios.get(url, { responseType: 'blob' });

      downloadCSV(response.data, `Breeding_${dayjs().format(DATE_FORMAT.DATE)}.xlsx`);
    } catch (error) {
      errorToastHandler(messages.FAILED_ON_DOWNLOAD_CSV);
    }
  }

  async function getTreatmentTypes() {
    try {
      await dispatch(fetchTreatmentTypes()).unwrap();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  async function getTreatmentUOM() {
    try {
      await dispatch(fetchTreatmentUOM()).unwrap();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  function deleteRecords() {
    const breedingIds = breeding?.filter(d => state.selectedIds.includes(d.id)).map(i => i.breeding_id);
    dispatch(deleteBreedings(breedingIds, state.selectedIds));

    if (!params.id) getBreeding();

    toggleModal('delete_modal');
  }

  function resetCommonData() {
    setState(prevState => ({
      ...prevState,
      common_data: {
        breeding_plan: '',
        breeding_status_id: structuredClone(breedingEventStatusTypesSelectOptions),
        calf_id: structuredClone(calfAry),
        calving_performance: '',
        comments: '',
        cost: '0',
        event_description: '',
        event_end_date: '',
        event_start_date: '',
        event_type_id: structuredClone(breedingEventTypesSelectOptions),
        expected_calving_date: '',
        is_pregnant: 0,
        maternity_performance: '',
        mating_type_id: structuredClone(matingTypes),
        sire_id: structuredClone(sireAry),
        straw_ref: '',
        straws: ''
      }
    }));
  }

  function toggleModal(modal) {
    resetCommonData();
    setState(prevState => ({ ...prevState, [modal]: !prevState[modal] }));

    if (modal === 'csv_modal') {
      formRef.current?.resetFields();
    }
  }

  function handleCreateCalvingEventType(newData, response) {
    const calvingEventType = breedingEventTypesSelectOptions.find(({ label }) => label === BREEDING_EVENT_TYPE.CALVING);
    const isCreatingCalving = newData.find(({ event_type_id }) => event_type_id === calvingEventType?.value);

    if (isCreatingCalving) {
      const [breeding] = response?.breeding_events;
      const animal = animals?.find(({ id }) => id === breeding.livestock_id);
      setState(prevState => ({ ...prevState, calv_registration_modal: true, calv_registration_animal: animal }));
    }
  }

  async function createBreedingForSelectedAnimals(data) {
    const records = initTableData?.filter(item => state.selectedIds.includes(item.id));
    const newData = records.map(record => ({
      ...data,
      event_end_date: data.event_end_date ? data.event_end_date.format(DATE_FORMAT.ISO_8601) : null,
      event_start_date: data.event_start_date.format(DATE_FORMAT.ISO_8601),
      expected_calving_date: data.expected_calving_date
        ? data.expected_calving_date.format(DATE_FORMAT.ISO_8601)
        : null,
      livestock_id: record.livestock_id
    }));

    try {
      const response = await dispatch(createManyBreeding(newData)).unwrap();
      handleCreateCalvingEventType(newData, response);
      setIsFormDirty(false);
      await getBreeding();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_CREATE_DATA);
    }
  }

  async function createBreedingForCurrentAnimal(data) {
    const newData = {
      ...data,
      event_end_date: data.event_end_date ? data.event_end_date.format(DATE_FORMAT.ISO_8601) : '',
      event_start_date: data.event_start_date ? data.event_start_date.format(DATE_FORMAT.ISO_8601) : '',
      expected_calving_date: data.expected_calving_date ? data.expected_calving_date.format(DATE_FORMAT.ISO_8601) : '',
      livestock_id: parseInt(params.id)
    };

    try {
      const response = await axios.post('breeding/store', newData);

      if (response.status === 200) {
        const calvingEventType = breedingEventTypesSelectOptions?.find(
          ({ label }) => label === BREEDING_EVENT_TYPE.CALVING
        );
        const isCreatingCalving = newData.event_type_id === calvingEventType.value;

        if (isCreatingCalving) {
          const breeding = response.data;
          const animal = animals?.find(({ id }) => id === breeding.livestock_id);
          setState(prevState => ({ ...prevState, calv_registration_modal: true, calv_registration_animal: animal }));
        }

        await getBreeding();
        setIsFormDirty(false);
      }
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_CREATE_DATA);
    }
  }

  async function handleCreateBreeding() {
    if (!state.selectedIds?.length && !params.id) return;

    const values = await formCreateRef.current?.validateFields();
    state.selectedIds?.length && !params.id
      ? await createBreedingForSelectedAnimals(values)
      : await createBreedingForCurrentAnimal(values);

    toggleModal('bulk_modal');
    formCreateRef.current?.resetFields();
  }

  const clearFilters = () => {
    setFilteredInfo({});
    dispatch(setSelectedFilters({}));
  };

  const setFilters = (pagination, filters) => {
    setFilteredInfo(filters);
    dispatch(setSelectedFilters(filters));
  };

  const handleTableChange = useCallback(
    (pagination, filters, sorter, { currentDataSource }) => {
      setFilters(pagination, filters);

      dispatch(setSortedData(currentDataSource));
    },
    [setFilteredInfo, dispatch]
  );

  const actions = useMemo(() => {
    const availableActions = [];

    if (hasPermission([PERMISSIONS.LIVESTOCK_BREEDING.CREATE], user.permissions)) {
      availableActions.push(
        {
          label: (
            <Button
              color="default"
              size="small"
              variant="link"
              disabled={!params.id && !state.selectedIds?.length}
              onClick={() => toggleModal('bulk_modal')}>
              Add breeding
            </Button>
          )
        },
        {
          label: (
            <Button color="default" size="small" variant="link" onClick={toggleUploadModal}>
              Upload breeding
            </Button>
          )
        }
      );
    }

    if (hasPermission([PERMISSIONS.LIVESTOCK_BREEDING.VIEW_OWN], user.permissions)) {
      availableActions.push({
        label: (
          <Button color="default" size="small" variant="link" onClick={downloadCSVFile}>
            Download breeding
          </Button>
        )
      });
    }

    if (hasPermission([PERMISSIONS.LIVESTOCK_BREEDING.DELETE_OWN], user.permissions)) {
      availableActions.push({
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!state.selectedIds?.length}
            onClick={() => toggleModal('delete_modal')}>
            Archive breeding
          </Button>
        )
      });
    }

    availableActions.push({
      label: (
        <Button
          color="default"
          size="small"
          variant="link"
          disabled={!Object.values(selectedFilters)?.length}
          onClick={clearFilters}>
          Clear all filters
        </Button>
      )
    });

    return availableActions;
  }, [state.selectedIds, selectedFilters, user.permissions]);

  const columnFilterOptions = useFilterOptions(sortedData);

  const columns = [
    {
      ...commonColumnProperties,
      title: 'Event ID',
      dataIndex: 'breeding_identifier',
      configColumnKey: TABLE_CONFIG_COLUMNS.EVENT_ID,
      filteredValue: filteredInfo.breeding_identifier || null,
      fixed: params.id ? 'left' : undefined,
      searchable: true,
      width: COLUMN_SIZE.LG,
      sorter: (a, b) => sortStrings(a.breeding_identifier, b.breeding_identifier)
    },
    {
      ...commonColumnProperties,
      title: 'Event Type',
      dataIndex: 'breeding_event_name',
      configColumnKey: TABLE_CONFIG_COLUMNS.EVENT_TYPE,
      editConfig: {
        type: EDIT_COMPONENT_TYPE.SELECT,
        options: breedingEventTypesSelectOptions
      },
      filteredValue: filteredInfo.breeding_event_name || null,
      filters: columnFilterOptions.types,
      width: COLUMN_SIZE.LG,
      onFilter: (value, record) =>
        value === BLANK_FILTER_TEXT ? !record?.breeding_event_name : record?.breeding_event_name === value,
      sorter: (a, b) => sortStrings(a.breeding_event_name, b.breeding_event_name)
    },
    {
      ...commonColumnProperties,
      title: 'Start Date',
      dataIndex: 'event_start_date',
      configColumnKey: TABLE_CONFIG_COLUMNS.START_DATE,
      editConfig: { type: EDIT_COMPONENT_TYPE.DATE_PICKER },
      filteredValue: filteredInfo.event_start_date || null,
      rules: [{ required: true, message: 'Required.' }],
      searchable: true,
      width: COLUMN_SIZE.LG,
      sorter: (a, b) => sortDates(a.event_start_date, b.event_start_date)
    },
    {
      ...commonColumnProperties,
      title: 'End Date',
      dataIndex: 'event_end_date',
      configColumnKey: TABLE_CONFIG_COLUMNS.END_DATE,
      editConfig: { type: EDIT_COMPONENT_TYPE.DATE_PICKER },
      filteredValue: filteredInfo.event_end_date || null,
      searchable: true,
      width: COLUMN_SIZE.LG,
      sorter: (a, b) => sortDates(a.event_end_date, b.event_end_date)
    },
    {
      ...commonColumnProperties,
      title: 'Expected Calving Date',
      dataIndex: 'expected_calving_date',
      configColumnKey: TABLE_CONFIG_COLUMNS.EXPECTED_CALVING_DATE,
      editConfig: { type: EDIT_COMPONENT_TYPE.DATE_PICKER },
      filteredValue: filteredInfo.expected_calving_date || null,
      searchable: true,
      width: COLUMN_SIZE.LG,
      sorter: (a, b) => sortDates(a.expected_calving_date, b.expected_calving_date)
    },
    {
      ...commonColumnProperties,
      title: 'Event Description',
      dataIndex: 'event_description',
      configColumnKey: TABLE_CONFIG_COLUMNS.EVENT_DESCRIPTION,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      editable: true,
      filteredValue: filteredInfo.event_description || null,
      searchable: true,
      width: COLUMN_SIZE.XXL,
      sorter: (a, b) => sortStrings(a.event_description, b.event_description)
    },
    {
      ...commonColumnProperties,
      title: 'Is Pregnant',
      dataIndex: 'is_pregnant',
      configColumnKey: TABLE_CONFIG_COLUMNS.IS_PREGNANT,
      editConfig: {
        type: EDIT_COMPONENT_TYPE.SELECT,
        options: [
          { label: 'Yes', value: 1 },
          { label: 'No', value: 0 }
        ]
      },
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false }
      ],
      filteredValue: filteredInfo.is_pregnant || null,
      width: COLUMN_SIZE.SM,
      onFilter: (value, record) => (value ? record.is_pregnant : !record.is_pregnant),
      sorter: (a, b) => {
        const strA = a.is_pregnant ? 'Yes' : '';
        const strB = b.is_pregnant ? 'Yes' : '';

        return sortStrings(strA, strB);
      }
    },
    {
      ...commonColumnProperties,
      title: 'Sire ID',
      dataIndex: 'sire_identifier_formatted',
      configColumnKey: TABLE_CONFIG_COLUMNS.SIRE_ID,
      editConfig: {
        type: EDIT_COMPONENT_TYPE.SELECT,
        options: sireAry
      },
      filteredValue: filteredInfo.sire_identifier_formatted || null,
      searchable: true,
      width: COLUMN_SIZE.XXL,
      sorter: (a, b) => sortStrings(a.sire_identifier_formatted, b.sire_identifier_formatted)
    },
    {
      ...commonColumnProperties,
      title: 'Calf ID',
      dataIndex: 'calf_identifier_formatted',
      configColumnKey: TABLE_CONFIG_COLUMNS.CALF_ID,
      editConfig: {
        type: EDIT_COMPONENT_TYPE.SELECT,
        options: calfAry
      },
      filteredValue: filteredInfo.calf_identifier_formatted || null,
      searchable: true,
      width: COLUMN_SIZE.XXL,
      sorter: (a, b) => sortStrings(a.calf_identifier_formatted, b.calf_identifier_formatted)
    },
    {
      ...commonColumnProperties,
      title: 'Mating Type',
      dataIndex: 'mating_type_name',
      configColumnKey: TABLE_CONFIG_COLUMNS.MATING_TYPE,
      editConfig: {
        type: EDIT_COMPONENT_TYPE.SELECT,
        options: matingTypes
      },
      filteredValue: filteredInfo.mating_type_name || null,
      searchable: true,
      width: COLUMN_SIZE.XL,
      sorter: (a, b) => sortStrings(a.mating_type_name, b.mating_type_name)
    },
    {
      ...commonColumnProperties,
      title: 'Straws',
      dataIndex: 'straws',
      configColumnKey: TABLE_CONFIG_COLUMNS.STRAWS,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      editable: true,
      filteredValue: filteredInfo.straws || null,
      searchable: true,
      width: COLUMN_SIZE.MD,
      sorter: (a, b) => sortStrings(a.straws, b.straws)
    },
    {
      ...commonColumnProperties,
      title: 'Straw Ref',
      dataIndex: 'straw_ref',
      configColumnKey: TABLE_CONFIG_COLUMNS.STRAW_REF,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      editable: true,
      filteredValue: filteredInfo.straw_ref || null,
      searchable: true,
      width: COLUMN_SIZE.MD,
      sorter: (a, b) => sortStrings(a.straw_ref, b.straw_ref)
    },
    {
      ...commonColumnProperties,
      title: 'Cost',
      dataIndex: 'cost',
      configColumnKey: TABLE_CONFIG_COLUMNS.COST,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT_NUMBER },
      editable: true,
      filteredValue: filteredInfo.cost || null,
      rules: [{ type: 'number' }],
      searchable: true,
      width: COLUMN_SIZE.MD,
      sorter: (a, b) => sortNumbers(a.cost, b.cost)
    },
    {
      ...commonColumnProperties,
      title: 'Breeding Status',
      dataIndex: 'breeding_status_name',
      configColumnKey: TABLE_CONFIG_COLUMNS.BREEDING_STATUS,
      editConfig: {
        type: EDIT_COMPONENT_TYPE.SELECT,
        options: breedingEventStatusTypesSelectOptions
      },
      filteredValue: filteredInfo.breeding_status_name || null,
      filters: columnFilterOptions.breedingStatuses,
      width: COLUMN_SIZE.XL,
      onFilter: (value, record) =>
        value === BLANK_FILTER_TEXT ? !record?.breeding_status_name : record?.breeding_status_name === value,
      sorter: (a, b) => sortStrings(a.breeding_status_name, b.breeding_status_name)
    },
    {
      ...commonColumnProperties,
      title: 'Calving Performance',
      dataIndex: 'calving_performance',
      configColumnKey: TABLE_CONFIG_COLUMNS.CALVING_PERFORMANCE,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      editable: true,
      filteredValue: filteredInfo.calving_performance || null,
      searchable: true,
      width: COLUMN_SIZE.LG,
      sorter: (a, b) => sortStrings(a.calving_performance, b.calving_performance)
    },
    {
      ...commonColumnProperties,
      title: 'Maternity Performance',
      dataIndex: 'maternity_performance',
      configColumnKey: TABLE_CONFIG_COLUMNS.MATERNITY_PERFORMANCE,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      editable: true,
      filteredValue: filteredInfo.maternity_performance || null,
      searchable: true,
      width: COLUMN_SIZE.LG,
      sorter: (a, b) => sortStrings(a.maternity_performance, b.maternity_performance)
    },
    {
      ...commonColumnProperties,
      title: 'Comments',
      dataIndex: 'comments',
      configColumnKey: TABLE_CONFIG_COLUMNS.COMMENTS,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      editable: true,
      filteredValue: filteredInfo.comments || null,
      searchable: true,
      width: COLUMN_SIZE.XXL,
      sorter: (a, b) => sortStrings(a.comments, b.comments)
    },
    {
      ...commonColumnProperties,
      title: 'Breeding Plan',
      dataIndex: 'breeding_plan',
      configColumnKey: TABLE_CONFIG_COLUMNS.BREEDING_PLAN,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      editable: true,
      filteredValue: filteredInfo.breeding_plan || null,
      searchable: true,
      width: COLUMN_SIZE.LG,
      sorter: (a, b) => sortStrings(a.breeding_plan, b.breeding_plan)
    }
  ];
  const livestockColumns = [
    {
      ...commonColumnProperties,
      title: 'Livestock ID',
      dataIndex: 'identifier',
      configColumnKey: TABLE_CONFIG_COLUMNS.LIVESTOCK_ID,
      filteredValue: filteredInfo.identifier || null,
      fixed: 'left',
      searchable: true,
      width: COLUMN_SIZE.MD,
      render: (value, record) => <Link to={`animal/${record.id}`}>{record.identifier}</Link>,
      sorter: (a, b) => sortStrings(a.identifier, b.identifier)
    },
    {
      ...commonColumnProperties,
      title: 'Mgmt Tag ID',
      dataIndex: 'eartag_management_id',
      configColumnKey: TABLE_CONFIG_COLUMNS.MGMT_TAG_ID,
      filteredValue: filteredInfo.eartag_management_id || null,
      searchable: true,
      width: COLUMN_SIZE.XL,
      sorter: (a, b) => sortStrings(a.eartag_management_id, b.eartag_management_id)
    },
    {
      ...commonColumnProperties,
      title: 'Sex',
      dataIndex: 'sex',
      configColumnKey: TABLE_CONFIG_COLUMNS.SEX,
      filteredValue: filteredInfo.sex || null,
      filters: columnFilterOptions?.sex,
      width: COLUMN_SIZE.LG,
      onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.sex : record?.sex === value),
      render: value => capitalize(value),
      sorter: (a, b) => sortStrings(a.sex, b.sex)
    },
    {
      ...commonColumnProperties,
      title: 'Colour',
      dataIndex: 'colour',
      configColumnKey: TABLE_CONFIG_COLUMNS.COLOUR,
      editConfig: {
        type: EDIT_COMPONENT_TYPE.INPUT
      },
      filteredValue: filteredInfo.colour || null,
      searchable: true,
      sortDirections: ['ascend', 'descend'],
      width: COLUMN_SIZE.LG,
      sorter: (a, b) => sortStrings(a.colour, b.colour)
    },
    {
      ...commonColumnProperties,
      title: 'Breed',
      dataIndex: 'breed',
      configColumnKey: TABLE_CONFIG_COLUMNS.BREED,
      filteredValue: filteredInfo.breed || null,
      filters: columnFilterOptions?.breeds,
      width: COLUMN_SIZE.XL,
      onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.breed : record?.breed === value),
      sorter: (a, b) => sortStrings(a.breed, b.breed)
    }
  ];

  const tableColumns = params.id ? [...columns] : [...livestockColumns, ...columns];

  function prepareTableData() {
    const breedingCopy = structuredClone(breeding);
    const breedingEventTypesSelectOptionsCopy = structuredClone(breedingEventTypesSelectOptions);
    const sireAryCopy = structuredClone(sireAry);
    const calfAryCopy = structuredClone(calfAry);
    const matingTypesCopy = structuredClone(matingTypes);
    const rowData = breedingCopy?.map(row => {
      row.event_type_id = breedingEventTypesSelectOptionsCopy?.map(x => {
        x.isSelected = x.value === row.event_type_id;
        return x;
      });
      row.breeding_status_id = breedingEventTypesSelectOptionsCopy?.map(x => {
        x.isSelected = x.value === row.breeding_status_id;
        return x;
      });
      row.mating_type_id = matingTypesCopy.map(x => {
        x.isSelected = x.value === row.mating_type_id;
        return x;
      });
      row.sire_id = sireAryCopy
        ?.filter(x => x.value !== row.livestock_id)
        .map(x => {
          x.isSelected = x.value === row.sire_id;
          return x;
        });
      row.calf_id = calfAryCopy
        ?.filter(x => x.value !== row.livestock_id)
        .map(x => {
          x.isSelected = x.value === row.calf_id;
          return x;
        });

      return row;
    });

    setTableData([...rowData]);
    setState(prevState => ({ ...prevState, mgmgList: breedingCopy.map(item => item.eartag_management_id) }));
    resetCommonData();
  }

  async function uploadCSV(csv) {
    try {
      const response = await axios.post('breeding/uploadCsv', { csv });

      if (response?.status === 200) {
        setState(prevState => ({ ...prevState, uploadSuccessMessage: response.data.message }));
        await getBreeding();

        toggleUploadModal();
        openUploadSuccessModal();
      }
    } catch (error) {
      errorToastHandler(messages.DEFAULT_ERROR);
    }
  }

  function selectRecords(selected) {
    setState(prevState => ({
      ...prevState,
      selectedIds: selected,
      selectAll: prevState.selectedIds?.length === selected?.length
    }));
  }

  async function handleChangeTableData([record]) {
    if (!record.breeding_id) {
      infoToastHandler(messages.CREATE_EVENT('breeding'));
      setUpdatedDataSource([...tableData]);

      return;
    }

    const initRecord = initTableData.find(i => i.id === record.id);
    const newData = {
      ...record,
      event_end_date: record.event_end_date ? dayjs(record.event_end_date).format(DATE_FORMAT.ISO_8601) : null,
      event_start_date: dayjs(record.event_start_date).format(DATE_FORMAT.ISO_8601),
      expected_calving_date: dayjs.isDayjs(record.expected_calving_date)
        ? dayjs(record.expected_calving_date).format(DATE_FORMAT.ISO_8601)
        : '',
      id: record.breeding_id,
      // TODO: Check why event_type_id & breeding_status_id are the same, and one is required, but another isn't
      event_type_id: record.breeding_event_name?.value || initRecord.event_type_id,
      breeding_status_id: record.breeding_status_name?.value,
      mating_type_id: record.mating_type_name?.value,
      sire_id: record.sire_identifier_formatted?.value,
      calf_id: record.calf_identifier_formatted?.value,
      is_pregnant: record.is_pregnant?.value
    };

    try {
      await dispatch(updateBreeding({ id: newData.breeding_id, data: newData })).unwrap();

      setIsFormDirty(false);
      successToastHandler(messages.UPDATED);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_UPDATE_DATA);
    }
  }

  const { NavigationPrompt, modalProps, nextLocation, confirmed, triggerUnsavedChangesPrompt } =
    useUnsavedChangesPrompt({
      when: isFormDirty,
      onSaveAndLeave: async () => {
        if (createFormSubmittable) {
          await handleCreateBreeding();

          return true;
        } else {
          warnToastHandler(messages.DATA_REQUIRED);
          formCreateRef.current?.validateFields();

          return false;
        }
      },
      onDiscardAndLeave: () => {
        formCreateRef.current?.resetFields();
        setIsFormDirty(false);
        setState(prev => ({ ...prev, bulk_modal: false }));
      }
    });

  useEffect(() => {
    if (!isFormDirty && nextLocation) {
      history.push(`${nextLocation.pathname}${nextLocation.search}`);
    }
  }, [isFormDirty, nextLocation, history]);

  const handleCancel = useCallback(() => {
    isFormDirty ? triggerUnsavedChangesPrompt() : setState(prev => ({ ...prev, bulk_modal: false }));
  }, [isFormDirty]);

  return (
    <>
      {NavigationPrompt}
      <div className={props.className}>
        <AppTable
          headerClass="py-2"
          searchPlaceholder="Search breeding"
          actions={actions}
          baseColumns={tableColumns}
          dataSource={tableData}
          description={props.breadcrumb}
          loading={loading || breedingLoading || measurementTypesLoading || matingEventLoading}
          searchable={props.searchable}
          tableConfigKey={TABLE_CONFIG_KEYS.LIVESTOCK_BREEDING}
          title={props.title}
          handleOnChange={handleTableChange}
          handleOnSelectRecords={selectRecords}
          handleSearchChange={getBreeding}
          updateTableData={handleChangeTableData}
          updatedDataSource={updatedDataSource}
        />
      </div>

      <Suspense fallback={null}>
        <CreateBreedingModal
          formRef={formCreateRef}
          data={state.common_data}
          isOpen={state.bulk_modal}
          submittable={createFormSubmittable}
          handleCancel={handleCancel}
          handleConfirm={handleCreateBreeding}
          onChange={setIsFormDirty}
          setCreateFormSubmittable={setCreateFormSubmittable}
        />

        <CalvRegistrationModal
          isOpen={state.calv_registration_modal}
          animal={state.calv_registration_animal}
          onCancel={() => {
            setIsFormDirty(false); // on cancellation need to clear the state, 'CreateBreedingModal' is closed at this stage
            toggleModal('calv_registration_modal');
          }}
          commonData={state.common_data}
          measureTypes={measurementTypes}
          treatmentTypes={treatmentTypesSelectOptions}
          treatmentUOM={treatmentUOMSelectOptions}
          breeds={breedsSelectOptions}
          mgmgList={state.mgmgList}
        />

        <AppModal
          isOpen={state.delete_modal}
          confirmButtonColor="danger"
          confirmButtonText="Archive"
          title="Archive breeding"
          handleCancel={() => toggleModal('delete_modal')}
          handleConfirm={deleteRecords}>
          <div className="py-4">{messages.ARCHIVE_CONFIRMATION('records')}</div>
        </AppModal>

        <UploadModal
          fileName="breeding-template.csv"
          formRef={formRef}
          isOpen={state.csv_modal}
          schema={BREEDING_CSV_VALIDATION_SCHEMA}
          submittable={submittable}
          template={BREEDING_CSV_TEMPLATE}
          onUpload={uploadCSV}
          onCancel={toggleUploadModal}
          setSubmittable={setSubmittable}
          description={
            <>
              <p className="mb-0">You can download the breeding list to input new breeding events.</p>
              <p className="mb-0">
                Download and save the file and then choose this file to upload new breeding events.
              </p>
            </>
          }
        />

        <AppModal
          confirmButtonText="Ok"
          cancelButtonHidden={true}
          isOpen={uploadSuccessModalVisible}
          handleConfirm={closeUploadSuccessModal}>
          <Flex align="center" justify="center" className="pb-2 pt-5">
            {state.uploadSuccessMessage}
          </Flex>
        </AppModal>

        <UnsavedChangesModal
          isOpen={modalProps.visible}
          handleCancel={modalProps.onCancel}
          handleConfirm={modalProps.onSave}
          handleDiscard={modalProps.onDiscard}
        />
      </Suspense>
    </>
  );
}

export default memo(BreedingTable);
